import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import config from '../../config';
import { ensureOwnListing, stringValuesToBooleans } from '../../util/data';
import { CriteoNewProduct, ListingLink } from '../../components';
import { EditListingPresentationForm } from '../../forms';
import css from './EditListingPresentationPanel.css';

const EditListingPresentationPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    isNew,
  } = props;
  useEffect(() => {
    if (!listing) return;

    setInitialValues(getInitialValues());
  }, [listing]);
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const getInitialValues = () => {
    const { listing } = props;
    const currentListing = ensureOwnListing(listing);
    const { geolocation, publicData } = currentListing.attributes;

    // Only render current search if full place object is available in the URL params
    // TODO bounds are missing - those need to be queried directly from Google Places
    const locationFieldsPresent =
      publicData && publicData.location && publicData.location.address && geolocation;
    const location = publicData && publicData.location ? publicData.location : {};
    const { address, city, addressDetails } = location;
    const { minCapacityToGuest, maxCapacityToGuest } = publicData;

    return {
      minCapacityToGuest: minCapacityToGuest ? minCapacityToGuest : 1,
      maxCapacityToGuest: maxCapacityToGuest ? maxCapacityToGuest : 1,
      location: locationFieldsPresent
        ? {
          search: address,
          selectedPlace: { address, origin: geolocation, city, addressDetails },
        }
        : null,
    };
  };
  const [initialValues, setInitialValues] = useState(getInitialValues());
  const panelTitle = currentListing.id ? (
    <FormattedMessage
      id="EditListingPresentationPanel.title"
      values={{ listingTitle: <ListingLink listing={listing}/> }}
    />
  ) : (
    <FormattedMessage id="EditListingPresentationPanel.createListingTitle"/>
  );
  const topAdviceText = <FormattedMessage id="EditListingPresentationPanel.topAdvice" />;
  const firstAdviceText = <FormattedMessage id="EditListingPresentationPanel.firstAdvice" />;
  const secondAdviceText = <FormattedMessage id="EditListingPresentationPanel.secondAdvice" />;
  const thirdAdviceText = <FormattedMessage id="EditListingPresentationPanel.thirdAdvice" />;
  const fourthAdviceText = <FormattedMessage id="EditListingPresentationPanel.fourthAdvice" />;
  const supportPhoneNumber = config.custom.supportPhoneNumberReadable;
  const bottomAdviceText = <FormattedMessage id="EditListingPresentationPanel.bottomAdvice" values={{ phoneNumber: supportPhoneNumber }} />;

  return (
    <div className={classes}>
      <CriteoNewProduct/>
      <h1 className={css.title}>{panelTitle}</h1>
      {isNew && (
        <div className={css.cautionMessage}>
          {topAdviceText}
          <ul className={css.advicesList}>
            <li>{firstAdviceText}</li>
            <li>{secondAdviceText}</li>
            <li>{thirdAdviceText}</li>
            <li>{fourthAdviceText}</li>
          </ul>
          {bottomAdviceText}
        </div>
      )}
      <EditListingPresentationForm
        className={css.form}
        initialValues={initialValues}
        saveActionMsg={submitButtonText}
        onSubmit={formValues => {
          const { location } = formValues;
          const {
            selectedPlace: { address, origin, city, addressDetails },
          } = location;
          setInitialValues({ ...initialValues, location: { search: address, selectedPlace: { address, origin, city, addressDetails } } });

          // Boolean fields are problematic in redux-form. Therefore, the boolean
          // fields use 'true' and 'false' string values which are mapped to actual
          // boolean values here.
          const values = stringValuesToBooleans(formValues);

          // map english value fields into french public data fields
          const {
            minCapacityToGuest,
            maxCapacityToGuest,
          } = values;

          // clear features if listing is not a private pool
          const publicData = {
            minCapacityToGuest: parseInt(minCapacityToGuest, 10),
            maxCapacityToGuest: parseInt(maxCapacityToGuest, 10),
            location: { address, city, addressDetails },
          };

          const updateValues = {
            publicData,
            geolocation: origin,
          };
          onSubmit(updateValues);
        }}
        onChange={onChange}
        updated={panelUpdated}
        updateError={errors.updateListingError}
        updateInProgress={updateInProgress}
      />
    </div>
  );
};

const { func, object, string, bool } = PropTypes;

EditListingPresentationPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingPresentationPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingPresentationPanel;



// WEBPACK FOOTER //
// ./src/components/EditListingPresentationPanel/index.js